import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'

// Import high-quality images from assets
import mobileImg from '../assets/images/mobile.png'
import newPlatformImg from '../assets/images/new-platform.png'
import newTableImg from '../assets/images/new-table.png'

/**
 * ClubOwner section component - featuring a rotating image carousel
 * Features: Auto-rotating carousel with manual navigation for club owner benefits
 */
const ClubOwner = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-50px" })
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  
  const images = [
    {
      src: mobileImg,
      alt: "Mobile poker interface"
    },
    {
      src: newPlatformImg, 
      alt: "New platform features"
    },
    {
      src: newTableImg,
      alt: "New table design"
    }
  ]

  // Auto-rotation effect (every 4.5 seconds - slower rotation)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      )
    }, 4500)

    return () => clearInterval(interval)
  }, [images.length])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      backgroundColor: 'transparent',
      border: 'none'
    },
    visible: {
      opacity: 1,
      y: 0,
      backgroundColor: 'transparent',
      border: 'none',
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  }

  const imageVariants = {
    enter: {
      opacity: 0,
      scale: 0.9,
      rotateY: 90
    },
    center: {
      opacity: 1,
      scale: 1,
      rotateY: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      rotateY: -90,
      transition: {
        duration: 0.4,
        ease: "easeIn"
      }
    }
  }

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    )
  }

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    )
  }

  const goToImage = (index) => {
    setCurrentImageIndex(index)
  }

  return (
    <section 
      id="club-owner" 
      className="relative min-h-screen overflow-hidden"
      style={{ backgroundColor: '#020d10' }}
      ref={ref}
    >
      <div style={{ 
        maxWidth: '1280px', 
        margin: '0 auto',
        padding: '80px 16px'
      }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Section Title */}
          <motion.div 
            className="text-center mb-16"
            variants={itemVariants}
          >
            <h2 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8"
              style={{
                fontFamily: 'Sequel100-65, sans-serif',
                textTransform: 'none',
                lineHeight: '1.1'
              }}
            >
              <span style={{ color: '#ffffff' }}>What you get as a </span>
              <span style={{ color: '#E5FF47' }}>Club Owner</span>
            </h2>
          </motion.div>

          {/* Carousel Container */}
          <motion.div 
            className="relative max-w-5xl mx-auto"
            variants={itemVariants}
          >
            <div className="relative overflow-hidden rounded-2xl" style={{ minHeight: '500px' }}>
              
              {/* Image Display */}
              <div className="relative w-full h-full flex items-center justify-center">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImageIndex}
                    src={images[currentImageIndex].src}
                    alt={images[currentImageIndex].alt}
                    className="max-w-full max-h-full object-contain"
                    style={{ 
                      height: '500px',
                      width: 'auto',
                      imageRendering: 'high-quality',
                      WebkitImageRendering: 'high-quality',
                      msInterpolationMode: 'bicubic'
                    }}
                    variants={imageVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                  />
                </AnimatePresence>
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-3 rounded-full transition-all duration-300 z-10"
                style={{ fontSize: '24px' }}
                aria-label="Previous image"
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ←
                </motion.div>
              </button>

              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-3 rounded-full transition-all duration-300 z-10"
                style={{ fontSize: '24px' }}
                aria-label="Next image"
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  →
                </motion.div>
              </button>

              {/* Dots Indicator */}
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToImage(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentImageIndex 
                        ? 'bg-yellow-400 scale-125' 
                        : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-6 w-full bg-gray-700 rounded-full h-1">
              <motion.div
                className="bg-gradient-to-r from-yellow-400 to-yellow-300 h-1 rounded-full"
                initial={{ width: "0%" }}
                animate={{ 
                  width: `${((currentImageIndex + 1) / images.length) * 100}%` 
                }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
          </motion.div>

          {/* Mobile styles handled by Tailwind responsive classes */}
        </motion.div>
      </div>
    </section>
  )
}

export default ClubOwner
